import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  AuthenticateWithRedirectCallback,
} from "@clerk/clerk-react";
import { dark } from "@clerk/themes";
import Layout from "./components/Layout";
import { ExtensionSync } from "./components/ExtensionSync";

// Lazy loading pages
const Welcome = lazy(() => import("./pages/Welcome"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Success = lazy(() => import("./pages/Success"));
const Cancel = lazy(() => import("./pages/Cancel"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkFrontendApi = import.meta.env.VITE_CLERK_FRONTEND_API_URL?.replace(/^https?:\/\//, '');

if (import.meta.env.PROD) {
  const isLiveKey = clerkPubKey?.startsWith('pk_live_');
  console.log(`[Clerk] Running in PRODUCTION mode. Key is ${isLiveKey ? 'LIVE' : 'TEST (ERROR!)'}`);
}

if (!clerkPubKey) {
  throw new Error("Missing Publishable Key");
}

const LoadingFallback = () => (
  <div className="min-h-screen bg-brand-dark flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
  </div>
);

export default function App() {
  return (
    <ClerkProvider 
      publishableKey={clerkPubKey} 
      frontendApi={clerkFrontendApi}
      appearance={{ baseTheme: dark }}
    >
      <BrowserRouter>
        <Layout>
          <ExtensionSync />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* SSO Callback */}
              <Route 
                path="/sso-callback" 
                element={<AuthenticateWithRedirectCallback signInUrl="/" />} 
              />

              {/* Protected Routes / Dashboard */}
              <Route
                path="/"
                element={
                  <>
                    <SignedIn>
                      <Dashboard />
                    </SignedIn>
                    <SignedOut>
                      <Welcome />
                    </SignedOut>
                  </>
                }
              />

              {/* Stripe Routes */}
              <Route
                path="/success"
                element={
                  <SignedIn>
                    <Success />
                  </SignedIn>
                }
              />
              <Route
                path="/cancel"
                element={
                  <SignedIn>
                    <Cancel />
                  </SignedIn>
                }
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <div style={{
            position: 'fixed', 
            bottom: '10px', 
            right: '10px', 
            background: 'red', 
            color: 'white', 
            padding: '5px', 
            zIndex: 9999, 
            fontSize: '12px',
            pointerEvents: 'none',
            opacity: 0.8
          }}>
            v37-RAM-MODE (Build: {new Date().toLocaleTimeString()})
          </div>
        </Layout>
      </BrowserRouter>
    </ClerkProvider>
  );
}
